import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Collapse, Descriptions, Table, Button, Spin, Tag, Space, Row, Col } from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined } from '@ant-design/icons';
import { PageTitle, StandardCard } from '../common';
import { theme } from '../../theme';
import api from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

const { Panel } = Collapse;

interface EmisionSilobolsaData {
  id: number;
  numero_silo: number;
  cargaenkilos: number;
  ubicacion: string;
  observaciones: string;
  fecha_alta: string;
  fecha_cierre: string;
  estado: string;
  empresa: {
    nombre: string;
    direccion: string;
  };
  sucursal: {
    nombre: string;
    direccion: string;
  };
  despachos: Array<{
    fecha: string;
    numero_remito: string;
    dtv: string;
    partesilobolsa: number;
    kilos_total_balanza: number;
    kilos_silos: number;
    cliente: {
      nombre: string;
      cuit: string;
    };
    nro_recdes: number;
  }>;
}

const EmisionSilobolsa: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [data, setData] = useState<EmisionSilobolsaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id && user?.id_empresa && user?.id_sucursal) {
      cargarDatosEmision();
    } else if (id && (!user?.id_empresa || !user?.id_sucursal)) {
      console.log('Esperando a que el usuario esté disponible...');
    } else if (!id) {
      setError('No se proporcionó un ID de silo bolsa');
      setLoading(false);
    }
  }, [id, user]);

  const cargarDatosEmision = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.id_empresa || !user?.id_sucursal) {
        throw new Error('No se pudo obtener el ID de empresa o sucursal del usuario');
      }
      
      if (!process.env.REACT_APP_SILOBOLSA_URL) {
        throw new Error('La variable de entorno REACT_APP_SILOBOLSA_URL no está configurada');
      }
      
      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_SILOBOLSA_URL}/emision_silobolsa/${user.id_empresa}/${user.id_sucursal}/${id}/`;
      
      const response = await api.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setData(response.data);
    } catch (error: any) {
      console.error('Error al cargar datos de emisión:', error);
      setError(error.response?.data?.error || error.message || 'Error al cargar los datos de emisión');
    } finally {
      setLoading(false);
    }
  };

  const formatFecha = (fecha: string) => {
    try {
      if (!fecha) return '-';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-ES');
    } catch {
      return fecha;
    }
  };

  const formatCuit = (cuit: string) => {
    if (!cuit) return '-';
    return cuit.replace(/(\d{2})(\d{8})(\d{1})/, '$1-$2-$3');
  };

  const getEstadoBadge = (estado: string) => {
    switch (estado) {
      case 'Activo':
        return <Tag color="success">Activo</Tag>;
      case 'Cerrado':
        return <Tag color="orange">Cerrado</Tag>;
      case 'Baja':
        return <Tag color="error">Baja</Tag>;
      default:
        return <Tag>{estado}</Tag>;
    }
  };

  const generarPDF = () => {
    if (!data) return;

    const doc = new jsPDF();
    doc.setFont('helvetica');
    doc.setFontSize(16);
    
    // Título principal
    const titulo = `EMISIÓN SILO BOLSA N° ${data.numero_silo}`;
    doc.text(titulo, 105, 20, { align: 'center' });
    
    let yPosition = 40;
    
    // DATOS DE LA EMPRESA
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DE LA EMPRESA', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Empresa: ${data.empresa?.nombre || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Dirección: ${data.empresa?.direccion || 'No especificado'}`, 20, yPosition);
    yPosition += 15;
    
    // DATOS DE LA SUCURSAL
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DE LA SUCURSAL', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Sucursal: ${data.sucursal?.nombre || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Dirección: ${data.sucursal?.direccion || 'No especificado'}`, 20, yPosition);
    yPosition += 15;
    
    // DATOS DEL SILO BOLSA
    doc.setFontSize(14);
    doc.setTextColor(135, 206, 235);
    doc.text('DATOS DEL SILO BOLSA', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Número Silo: ${data.numero_silo}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Ubicación: ${data.ubicacion || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Kilos: ${data.cargaenkilos.toLocaleString('es-ES', { minimumFractionDigits: 2 })} kg`, 20, yPosition);
    yPosition += 8;
    doc.text(`Fecha Alta: ${formatFecha(data.fecha_alta)}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Fecha Cierre: ${formatFecha(data.fecha_cierre)}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Estado: ${data.estado}`, 20, yPosition);
    yPosition += 15;
    
    // DESPACHOS REALIZADOS
    if (data.despachos && data.despachos.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.setTextColor(135, 206, 235);
      doc.text('DESPACHOS REALIZADOS', 105, 20, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      
      const despachosData = data.despachos.map(despacho => [
        formatFecha(despacho.fecha),
        despacho.numero_remito || '-',
        despacho.dtv || '-',
        `${despacho.partesilobolsa.toFixed(2)}%`,
        despacho.kilos_total_balanza.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
        despacho.kilos_silos.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
        despacho.cliente?.nombre || 'No especificado'
      ]);
      
      // Calcular totales para el PDF
      const totalBalanzaPDF = data.despachos.reduce((sum, despacho) => sum + (despacho.kilos_total_balanza || 0), 0);
      const totalSiloPDF = data.despachos.reduce((sum, despacho) => sum + (despacho.kilos_silos || 0), 0);
      
      // Agregar fila de totales
      const totalesRow = [
        'TOTAL:',
        '',
        '',
        '',
        totalBalanzaPDF.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
        totalSiloPDF.toLocaleString('es-ES', { minimumFractionDigits: 2 }),
        ''
      ];
      
      autoTable(doc, {
        head: [['Fecha', 'N° Remito', 'DTV', 'Parte Silo', 'Kilos Balanza', 'Kilos Silo', 'Cliente']],
        body: [...despachosData, totalesRow],
        startY: 30,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [135, 206, 235],
          textColor: 255,
          fontStyle: 'bold',
        },
        footStyles: {
          fillColor: [250, 250, 250],
          textColor: 0,
          fontStyle: 'bold',
        },
        didParseCell: (data) => {
          // Estilo para la fila de totales
          if (data.row.index === despachosData.length && data.column.index === 0) {
            data.cell.styles.fillColor = [250, 250, 250];
            data.cell.styles.fontStyle = 'bold';
          }
          if (data.row.index === despachosData.length && data.column.index >= 4) {
            data.cell.styles.fillColor = [250, 250, 250];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });
    }
    
    // Guardar PDF
    const fileName = `Emision_Silobolsa_${data.numero_silo}_${dayjs().format('YYYY-MM-DD')}.pdf`;
    doc.save(fileName);
  };

  const volver = () => {
    navigate('/listado_silobolsa');
  };

  // Calcular totales de Kilos Total Balanza y Kilos Silo (hooks deben ir antes de returns)
  const totales = useMemo(() => {
    if (!data?.despachos || data.despachos.length === 0) {
      return { totalBalanza: 0, totalSilo: 0 };
    }
    
    const totalBalanza = data.despachos.reduce((sum, despacho) => {
      return sum + (despacho.kilos_total_balanza || 0);
    }, 0);
    
    const totalSilo = data.despachos.reduce((sum, despacho) => {
      return sum + (despacho.kilos_silos || 0);
    }, 0);
    
    return { totalBalanza, totalSilo };
  }, [data?.despachos]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" tip="Cargando datos de emisión..." />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: '40px' }}>
        <StandardCard 
          title={error ? 'Error' : 'No se encontraron datos'}
          bodyStyle={{ padding: '24px' }}
        >
          <p style={{ marginBottom: '16px', fontSize: '13px' }}>
            {error || 'No se pudieron cargar los datos de emisión.'}
          </p>
          <Button type="primary" onClick={volver} size="middle" style={{ height: '32px', fontSize: '13px' }}>
            Volver al listado
          </Button>
        </StandardCard>
      </div>
    );
  }

  // Configuración de columnas para la tabla de despachos
  const despachosColumns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      align: 'center' as const,
      render: (fecha: string) => formatFecha(fecha),
    },
    {
      title: 'N° Remito',
      dataIndex: 'numero_remito',
      key: 'numero_remito',
      width: 120,
      align: 'center' as const,
      render: (value: string) => value || '-',
    },
    {
      title: 'DTV',
      dataIndex: 'dtv',
      key: 'dtv',
      width: 120,
      align: 'center' as const,
      render: (value: string) => value || '-',
    },
    {
      title: 'Parte Silo',
      dataIndex: 'partesilobolsa',
      key: 'partesilobolsa',
      width: 100,
      align: 'center' as const,
      render: (value: number) => `${value?.toFixed(2) || '0.00'}%`,
    },
    {
      title: 'Kilos Total Balanza',
      dataIndex: 'kilos_total_balanza',
      key: 'kilos_total_balanza',
      width: 150,
      align: 'right' as const,
      render: (value: number) => value?.toLocaleString('es-ES', { minimumFractionDigits: 2 }) || '-',
    },
    {
      title: 'Kilos Silo',
      dataIndex: 'kilos_silos',
      key: 'kilos_silos',
      width: 120,
      align: 'right' as const,
      render: (value: number) => value?.toLocaleString('es-ES', { minimumFractionDigits: 2 }) || '-',
    },
    {
      title: 'Cliente',
      dataIndex: ['cliente', 'nombre'],
      key: 'cliente',
      width: 200,
    },
    {
      title: 'N° Rec/Des',
      dataIndex: 'nro_recdes',
      key: 'nro_recdes',
      width: 100,
      align: 'center' as const,
    },
  ];

  return (
    <div style={{ padding: '0' }}>
      {/* Header con título y botones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <PageTitle style={{ marginBottom: 0 }}>
          EMISIÓN SILO BOLSA N° {data.numero_silo}
        </PageTitle>
        <Space>
          <Button 
            type="primary" 
            icon={<FilePdfOutlined />}
            onClick={generarPDF} 
            size="middle"
            style={{ height: '32px', fontSize: '13px' }}
          >
            Generar PDF
          </Button>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={volver} 
            size="middle"
            style={{ height: '32px', fontSize: '13px' }}
          >
            Volver
          </Button>
        </Space>
      </div>

      {/* Carátula con datos de empresa y sucursal */}
      <StandardCard bodyStyle={{ padding: '16px' }}>
        <Collapse defaultActiveKey={['empresa', 'sucursal', 'silobolsa']} ghost>
          <Panel header="Datos de la Empresa" key="empresa">
            <Descriptions 
              column={{ xs: 1, sm: 2, md: 2 }}
              size="small"
              labelStyle={{ fontSize: '12px', fontWeight: 600 }}
              contentStyle={{ fontSize: '13px' }}
            >
              <Descriptions.Item label="Empresa">{data.empresa?.nombre || '-'}</Descriptions.Item>
              <Descriptions.Item label="Dirección" span={2}>{data.empresa?.direccion || '-'}</Descriptions.Item>
            </Descriptions>
          </Panel>
          <Panel header="Datos de la Sucursal" key="sucursal">
            <Descriptions 
              column={{ xs: 1, sm: 2, md: 2 }}
              size="small"
              labelStyle={{ fontSize: '12px', fontWeight: 600 }}
              contentStyle={{ fontSize: '13px' }}
            >
              <Descriptions.Item label="Sucursal">{data.sucursal?.nombre || '-'}</Descriptions.Item>
              <Descriptions.Item label="Dirección" span={2}>{data.sucursal?.direccion || '-'}</Descriptions.Item>
            </Descriptions>
          </Panel>
          <Panel header="Datos del Silo Bolsa" key="silobolsa">
            <Descriptions 
              column={{ xs: 1, sm: 2, md: 2 }}
              size="small"
              labelStyle={{ fontSize: '12px', fontWeight: 600 }}
              contentStyle={{ fontSize: '13px' }}
            >
              <Descriptions.Item label="Número Silo">{data.numero_silo}</Descriptions.Item>
              <Descriptions.Item label="Estado">{getEstadoBadge(data.estado)}</Descriptions.Item>
              <Descriptions.Item label="Ubicación">{data.ubicacion || '-'}</Descriptions.Item>
              <Descriptions.Item label="Kilos">
                {data.cargaenkilos.toLocaleString('es-ES', { minimumFractionDigits: 2 })} kg
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Alta">{formatFecha(data.fecha_alta)}</Descriptions.Item>
              <Descriptions.Item label="Fecha Cierre">{formatFecha(data.fecha_cierre)}</Descriptions.Item>
              {data.observaciones && (
                <Descriptions.Item label="Observaciones" span={2}>{data.observaciones}</Descriptions.Item>
              )}
            </Descriptions>
          </Panel>
        </Collapse>
      </StandardCard>

      {/* Tabla de Despachos (Camiones) */}
      {data.despachos && data.despachos.length > 0 && (
        <StandardCard 
          title="Despachos Realizados (Camiones)"
          bodyStyle={{ padding: '12px' }}
          style={{ marginTop: '16px' }}
        >
          <Table
            columns={despachosColumns}
            dataSource={data.despachos.map((despacho, index) => ({ ...despacho, key: index }))}
            pagination={false}
            size="middle"
            scroll={{ x: 'max-content' }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={4} align="right">
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>TOTAL:</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>
                      {totales.totalBalanza.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>
                      {totales.totalSilo.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} colSpan={2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </StandardCard>
      )}

      {/* Mensaje si no hay despachos */}
      {(!data.despachos || data.despachos.length === 0) && (
        <StandardCard 
          bodyStyle={{ padding: '24px', textAlign: 'center' }}
          style={{ marginTop: '16px' }}
        >
          <p style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
            No se encontraron despachos para este silo bolsa.
          </p>
        </StandardCard>
      )}
    </div>
  );
};

export default EmisionSilobolsa;

